import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon1 from '../assets/Trefle.svg';
import Icon2 from '../assets/Coeur.svg';
import C1 from "../assets/partouche_multiverse.png"
import C2 from "../assets/joker_club.png"
import C3 from "../assets/groupe_partouche.png"


gsap.registerPlugin(ScrollTrigger);

const Section = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  overflow:hidden;
  @media (max-width: 900px) { padding-top: 4rem;  }
`;

const TitreContainer = styled.div`
  margin-top: 2%;
  width: 80%;
`;

const Title = styled.p`
  font-size: 8rem;
  font-family: 'MongoGrotesqueLight';
  color: #fff;
  margin: 0;
  white-space: pre-wrap;
  line-height: 0.9;
  @media (max-width: 900px) { display: none; }
`;

const TitleMob = styled.p`
  display: none;
  @media (max-width: 900px) {
    display: block;
    font-size: 5rem;
    font-family: 'MongoGrotesqueLight';
    color: #fff;
    margin: 0;
    white-space: pre-wrap;
    line-height: 0.9;
  }
  @media (max-width: 415px) { font-size: 4rem; }
`;

const SubtitleWrapper = styled.div`
  overflow: hidden;
  height: 50px;
  margin: 0.5rem 0 1rem;
  @media (max-width: 1024px) { height: 200px; }
  @media (max-width: 760px)  { height: 100px; }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: #fff;
  margin: 0;
`;

const SideIconBox = styled.div`
  position: absolute;
  top: 45%;
  width: 250px;     
  height: 250px;    
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;    
  user-select: none;
  @media (max-width: 900px) { display: none; }
`;

const SideIconLeft  = styled(SideIconBox)` left: -1%;  transform: rotate(25deg);  `;
const SideIconRight = styled(SideIconBox)` right: -1%; transform:scale(0.8);`;

const SideIconImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;      /* garde le ratio, remplit la boîte */
  backface-visibility: hidden;
`;
const LargeIcon = styled.img`
  position: absolute;
  top: 40%;
  left: 0;
  width: 250px;
  height: 250px;
  transform: rotate(15deg);
  @media (max-width: 900px) { display: none; }
`;

const LargeIcon2 = styled.img`
  position: absolute;
  top: 40%;
  right: 0;
  width: 250px;
  height: 250px;
  @media (max-width: 900px) { display: none; }
`;

const CardMask = styled.div`
  width: 100%;
  height: calc(var(--vh) * 100);
  overflow: hidden;
  position: relative;


  @media (max-width: 900px) {
    height: calc(var(--vh) * 90);
  }
`;

const ImageStack = styled.div`
  display: grid;
  grid-template-areas: 'stack';
  place-items: center;
  margin-top: 4rem;
  @media (max-width: 900px) { margin-top: 4rem; }
`;

const LayerWrapper = styled.div<{ angle: number; offset: number }>`
  grid-area: stack;
  transform: translateX(${({ offset }) => offset}px)
             rotate(${({ angle }) => angle}deg);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  will-change: transform;
  backface-visibility: hidden;
`;

const LayerImage = styled.img`
  width: 330px;
  height: 450px;
  @media (max-width: 900px) { width: 250px; height: 390px; }
`;

const OverlayButton = styled.button`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 10px;
  font-size: 1rem;
  border: none;
  border-radius: 20px;
  background: #FFE6DA;
  color: #000;
  cursor: pointer;
`;

const layers = [
  { angle: -9.58, offset: 10 },
  { angle: -2,     offset: 3  },
  { angle:  9.58,  offset: -5 },
];

const Tradition: React.FC = () => {
  const sectionRef  = useRef<HTMLDivElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const maskRef     = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty(
        '--vh',
        `${window.innerHeight * 0.01}px`
      );
      ScrollTrigger.refresh();
    };
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    setVh();
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

useLayoutEffect(() => {
  const sectionEl = sectionRef.current;
  const maskEl    = maskRef.current;
  if (!sectionEl || !maskEl) return;

  // subtitle trigger left unchanged…

  // 1) grab all your cards and mask height
  const cards = gsap.utils.toArray<HTMLElement>('.layer');
  const maskH = maskEl.clientHeight;

  // 2) offset each card by mask + its own height
  cards.forEach(card => {
    const cardH = card.clientHeight;
    gsap.set(card, { y: maskH + cardH });
  });

  // 3) build your pinned timeline exactly as before
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sectionEl,
      start: () => {
        const offset = Math.round(
          window.innerHeight * (isMobile ? 0.55 : 0.35)
        );
        return `top+=${offset} top`;
      },
      end: () => `+=${maskH}`,
      scrub: 1.5,
      pin: true,
      pinSpacing: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
    },
  });

  // 4) animate each back to y:0
  cards.forEach(card => {
    tl.to(card, {
      y: 0,
      duration: 3,
      ease: 'power2.out',
    });
  });

  // 5) refresh on late image loads
  ScrollTrigger.batch(maskEl.querySelectorAll('img'), {
    once: true,
    onEnter: () => ScrollTrigger.refresh(),
  });

  return () => {
    ScrollTrigger.getAll().forEach(st => st.kill());
    tl.kill();
  };
}, [isMobile]);

const cards = [
  { image: C1, alt: 'Card 1' },
  { image: C2, alt: 'Card 2' },
  { image: C3, alt: 'Card 3' },
];

  return (
    <Section ref={sectionRef}>
      <PageContainer>
        <TitreContainer>
          <Title>
            DE LA TRADITION DU JEU
            <br />
            À L'INNOVATION IMMERSIVE
          </Title>
          <TitleMob>
            DE LA TRADITION DU JEU
            À L'INNOVATION<br />
            IMMERSIVE
          </TitleMob>
          <SubtitleWrapper>
            <Subtitle ref={subtitleRef}>
              Découvrez comment le groupe Partouche façonne l'avenir du
              <br />
              divertissement avec Partouche Multiverse et Joker Club.
            </Subtitle>
          </SubtitleWrapper>
        </TitreContainer>
        <SideIconLeft>
        <SideIconImg src={Icon1} alt="Icon 1" />
      </SideIconLeft>
      <SideIconRight>
        <SideIconImg src={Icon2} alt="Icon 2" />
      </SideIconRight>

        <CardMask ref={maskRef}>
          <ImageStack>
            {layers.map((layer, i) => (
              <LayerWrapper
                key={i}
                className="layer"
                angle={layer.angle}
                offset={layer.offset}
              >
                <LayerImage src={cards[i].image} alt={cards[i].alt} />
                {/* <OverlayButton>Découvrir</OverlayButton> */}
              </LayerWrapper>
            ))}
          </ImageStack>
        </CardMask>
      </PageContainer>
    </Section>
  );
};

export default Tradition;